import type { ErasureStatus } from '../dataProvider/types';

/**
 * Grace-window helpers, lifecycle vocabulary, and the per-type DISPOSITION POLICY for the erasure
 * surface (D-erasure, Phase 7 surface 6). The grace deadline is DERIVED off the injected clock, the
 * established DSAR-statutory-deadline / suspension-elapse / impersonation-expiry pattern: a
 * requestedAt plus a fixed grace window gives an end instant, and the clock places a request
 * in_grace or elapsed. NO stored grace state and NO deletion engine.
 */

const DAY = 86_400_000;

/**
 * The grace window an erasure request waits through before it becomes executable, in milliseconds.
 * [REVIEW] the length with counsel and the compliance team: a cancellable cooling-off period before
 * an IRREVERSIBLE act is the point, so the exact length is the platform's to confirm. It lives here
 * as one adjustable constant.
 */
export const GRACE_WINDOW_MS = 30 * DAY;

/** The ISO instant a request lodged at `requestedAt` finishes its grace window. */
export const graceEndsAt = (requestedAt: string): string =>
  new Date(new Date(requestedAt).getTime() + GRACE_WINDOW_MS).toISOString();

/**
 * The derived grace state for an OPEN (requested) erasure request:
 *  - in_grace: the clock is still inside the grace window. The request is cancellable, NOT yet
 *    executable.
 *  - elapsed: the grace window has passed. The request is now executable (by super_admin).
 * A resolved request (executed / cancelled / rejected) has no live grace, so the model carries null.
 */
export type GracePhase = 'in_grace' | 'elapsed';

export const gracePhase = (requestedAt: string, now: number): GracePhase =>
  now > new Date(graceEndsAt(requestedAt)).getTime() ? 'elapsed' : 'in_grace';

/** Human label per erasure status. */
export const ERASURE_STATUS_LABEL: Record<ErasureStatus, string> = {
  requested: 'Requested',
  executed: 'Executed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

export const ERASURE_STATUS_COLOR: Record<
  ErasureStatus,
  'default' | 'info' | 'success' | 'error'
> = {
  requested: 'info',
  executed: 'error',
  cancelled: 'default',
  rejected: 'error',
};

/** Chip label per derived grace phase. */
export const GRACE_PHASE_LABEL: Record<GracePhase, string> = {
  in_grace: 'In grace window',
  elapsed: 'Grace elapsed, executable',
};

export const GRACE_PHASE_COLOR: Record<
  GracePhase,
  'default' | 'warning' | 'error'
> = {
  in_grace: 'warning',
  elapsed: 'error',
};

/**
 * What erasure does to a record type. The PRINCIPLE: erase the PERSON, RETAIN what law or a
 * legitimate interest requires (anonymised, the person severed), NEVER touch the audit trail; when in
 * doubt anonymise-retain rather than delete (anonymise preserves optionality, delete forecloses it).
 *  - tombstone_anonymise: the member's own row, scrubbed of identifying PII, the row and id KEPT.
 *  - anonymise_retain: the person is severed but the record is kept (a tax / AML / financial /
 *    proof-of-consent obligation, or a conservative default).
 *  - delete: the record is removed outright (no retention value).
 *  - untouched: never reached by erasure (the audit trail).
 */
export type Disposition =
  | 'tombstone_anonymise'
  | 'anonymise_retain'
  | 'delete'
  | 'untouched';

export interface DispositionRule {
  /** The record-type key (matches the count key the preview computes). */
  key: string;
  label: string;
  disposition: Disposition;
  /**
   * True when the MOCK performs this transformation itself on execute (the minimal owned-row
   * tombstone the mock can fully own); false when it is the BACKEND seam (the real anonymise-retain
   * or delete cascade the mock does not fake).
   */
  mockOwned: boolean;
  note: string;
}

/**
 * The per-type disposition policy the preview renders (D-erasure). ADJUSTABLE: the precise legal line
 * (which records are retained, for how long) is the platform's to confirm with counsel, so this table
 * is the one place it lives (the DSAR-inventory adjustability precedent). Only `profile` and
 * `transactions` are mock-owned (the User row and the two NULLABLE links, transactions.userId and the
 * profile's referredBy); every other anonymise-retain or delete is the backend seam.
 */
export const DISPOSITION_POLICY: readonly DispositionRule[] = [
  {
    key: 'profile',
    label: 'Member profile',
    disposition: 'tombstone_anonymise',
    mockOwned: true,
    note: 'Identifying fields are scrubbed to the erased marker and the referral link is severed; the row and its id are kept so retained records resolve to a tombstone.',
  },
  {
    key: 'transactions',
    label: 'Transactions',
    disposition: 'anonymise_retain',
    mockOwned: true,
    note: 'The member link is severed (userId set to null); the financial record is kept.',
  },
  {
    key: 'cashoutRequests',
    label: 'Cashouts',
    disposition: 'anonymise_retain',
    mockOwned: false,
    note: 'Retained for tax (1099-K) and AML obligations; the person is severed by the backend.',
  },
  {
    key: 'balanceLedger',
    label: 'Balance ledger',
    disposition: 'anonymise_retain',
    mockOwned: false,
    note: 'Financial record retained; the person is severed by the backend.',
  },
  {
    key: 'welcomeBonusFundings',
    label: 'Welcome bonuses',
    disposition: 'anonymise_retain',
    mockOwned: false,
    note: 'Financial record retained; the person is severed by the backend.',
  },
  {
    key: 'sanctionsMatches',
    label: 'Sanctions matches',
    disposition: 'anonymise_retain',
    mockOwned: false,
    note: 'Retained as AML proof; the person is severed by the backend.',
  },
  {
    key: 'consents',
    label: 'Consent records',
    disposition: 'anonymise_retain',
    mockOwned: false,
    note: 'Retained as proof of consent; the person is severed by the backend.',
  },
  {
    key: 'tickets',
    label: 'Support tickets',
    disposition: 'anonymise_retain',
    mockOwned: false,
    note: 'Retained conservatively; the person is severed by the backend.',
  },
  {
    key: 'dsarRequests',
    label: 'Prior data requests',
    disposition: 'anonymise_retain',
    mockOwned: false,
    note: 'Retained conservatively; the person is severed by the backend.',
  },
  {
    key: 'clicks',
    label: 'Clicks',
    disposition: 'delete',
    mockOwned: false,
    note: 'Behavioural telemetry with no retention value; deleted by the backend.',
  },
  {
    key: 'auditLog',
    label: 'Audit trail',
    disposition: 'untouched',
    mockOwned: false,
    note: 'The tamper-evidence record is never erased; the erasure action itself is audited into it.',
  },
];

export const DISPOSITION_LABEL: Record<Disposition, string> = {
  tombstone_anonymise: 'Tombstone (scrub PII, keep the row)',
  anonymise_retain: 'Anonymise and retain',
  delete: 'Delete',
  untouched: 'Untouched',
};

export const DISPOSITION_COLOR: Record<
  Disposition,
  'default' | 'warning' | 'error' | 'success' | 'info'
> = {
  tombstone_anonymise: 'warning',
  anonymise_retain: 'info',
  delete: 'error',
  untouched: 'success',
};
